import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { AlertTriangle, Trash2, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * Super-admin-only "Clear test data" button + double-confirmation dialog.
 * The button is hidden for non-super-admins.
 *
 * Props:
 *   - role: current user role
 *   - title: button label (e.g. "Clear Approved + Rejected")
 *   - description: 1-line subtitle inside the modal
 *   - endpoint: backend DELETE path (e.g. "/admin/enrollment-requests/clear-resolved")
 *   - confirmPhrase: phrase the admin must type before the Delete button enables
 *                    (defaults to "CLEAR")
 *   - onCleared(deletedCount): called after a successful delete so caller can refresh
 *   - dataTestId: prefix for data-testid attrs on the button + dialog
 */
export default function ClearTestDataButton({
  role,
  title,
  description,
  endpoint,
  confirmPhrase = "CLEAR",
  onCleared,
  dataTestId = "clear-data",
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [typed, setTyped] = useState("");

  if (role !== "super_admin") return null;

  const submit = async () => {
    if (typed !== confirmPhrase) return;
    setBusy(true);
    try {
      const r = await api.delete(endpoint);
      toast.success(`Cleared ${r.data?.deleted_count ?? 0} record(s).`);
      setOpen(false);
      setTyped("");
      onCleared?.(r.data?.deleted_count ?? 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not clear data.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        variant="outline"
        className="h-9 border-rose-300 text-rose-700 hover:bg-rose-50 dark:border-rose-700 dark:text-rose-300 dark:hover:bg-rose-900/30"
        onClick={() => setOpen(true)}
        data-testid={`${dataTestId}-open-btn`}
      >
        <Trash2 className="w-4 h-4 mr-1.5" /> {title}
      </Button>

      <Dialog open={open} onOpenChange={(v) => { if (!busy) { setOpen(v); if (!v) setTyped(""); } }}>
        <DialogContent className="max-w-md" data-testid={`${dataTestId}-dialog`}>
          <DialogHeader>
            <div className="mx-auto mb-2 h-12 w-12 rounded-full bg-rose-100 dark:bg-rose-500/15 grid place-items-center">
              <AlertTriangle className="w-6 h-6 text-rose-600 dark:text-rose-400" />
            </div>
            <DialogTitle className="font-display text-center text-xl">
              Clear test data?
            </DialogTitle>
            <DialogDescription className="text-center">
              {description} <br />
              <span className="font-semibold text-rose-700 dark:text-rose-300">
                This action is permanent and cannot be undone.
              </span>
            </DialogDescription>
          </DialogHeader>

          <div className="mt-3">
            <label className="text-xs font-semibold text-slate-600 dark:text-slate-300">
              Type <span className="font-bold text-rose-700 dark:text-rose-300">{confirmPhrase}</span> to confirm
            </label>
            <input
              autoFocus
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              className="mt-1 w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-slate-900 dark:bg-slate-900 dark:border-slate-700 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-rose-300"
              placeholder={confirmPhrase}
              data-testid={`${dataTestId}-input`}
            />
          </div>

          <DialogFooter className="flex flex-col-reverse sm:flex-row gap-2 mt-2">
            <Button
              variant="outline"
              className="w-full sm:w-auto h-10"
              onClick={() => { setOpen(false); setTyped(""); }}
              disabled={busy}
              data-testid={`${dataTestId}-cancel-btn`}
            >
              Cancel
            </Button>
            <Button
              className="w-full sm:w-auto h-10 bg-rose-600 hover:bg-rose-700 text-white"
              disabled={typed !== confirmPhrase || busy}
              onClick={submit}
              data-testid={`${dataTestId}-confirm-btn`}
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Trash2 className="w-4 h-4 mr-1.5" />}
              Delete permanently
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
